import React from 'react';
import {
  Gavel, Users, BookOpen, MessageCircle, FileSearch, Pencil, CheckSquare, TrendingUp,
} from 'lucide-react';
import type { WorkItem } from '../../types/models';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';

// ---------------------------------------------------------------------------
// WorkItemTypeTreatment — §5.5 from the improvement doc.
// "A Decision should not look like a Task. A Briefing should show who is
// briefed and when." Each work item type gets a distinct icon + inline
// secondary hint so the eye can sort by type at glance — without redesigning
// every surface that shows work items.
// ---------------------------------------------------------------------------

const TYPE_META = {
  Task:     { Icon: CheckSquare,  colour: '#6C9BFF', label: 'Task' },
  Decision: { Icon: Gavel,        colour: '#FF8A65', label: 'Decision' },
  Briefing: { Icon: Users,        colour: '#69F0AE', label: 'Briefing' },
  Review:   { Icon: FileSearch,   colour: '#FFB74D', label: 'Review' },
  FollowUp: { Icon: MessageCircle, colour: '#CE93D8', label: 'Follow-up' },
  Draft:    { Icon: Pencil,       colour: '#4DD0E1', label: 'Draft' },
  Reading:  { Icon: BookOpen,     colour: '#AED581', label: 'Reading' },
} as const;

interface BadgeProps {
  type: WorkItem['type'];
  variant?: 'compact' | 'full';
}

export function WorkItemTypeBadge({ type, variant = 'full' }: BadgeProps) {
  const meta = TYPE_META[type];
  if (!meta) return null;
  const { Icon, colour, label } = meta;

  if (variant === 'compact') {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full"
              style={{ backgroundColor: `${colour}22`, color: colour }}
            >
              <Icon className="h-3.5 w-3.5" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <span
      className="inline-flex h-[22px] items-center gap-1 rounded-full px-2 text-[0.72rem] font-semibold"
      style={{ backgroundColor: `${colour}1F`, color: colour }}
    >
      <Icon className="h-3.5 w-3.5" style={{ color: colour }} />
      {label}
    </span>
  );
}

export function workItemTypeHint(item: WorkItem, driverTitle?: string | null): string | null {
  switch (item.type) {
    case 'Briefing':
      if (item.assignedToExecutiveId) return 'Briefing for an executive';
      return 'Briefing pack';
    case 'Decision':
      return driverTitle ? `Decision · aligned to ${driverTitle}` : 'Decision';
    case 'Reading':
      return `Reading · fits a low-energy block (${item.estimatedMinutes} min)`;
    case 'FollowUp':
      {
        const firstLine = (item.notes ?? '').split('\n')[0]?.trim();
        if (firstLine && firstLine.length <= 80) return `Follow-up · ${firstLine}`;
        return 'Follow-up chase';
      }
    case 'Review':
      return 'Review';
    case 'Draft':
      return 'Draft';
    case 'Task':
    default:
      return null;
  }
}

interface InlineProps {
  item: WorkItem;
  showBadge?: boolean;
  /** The item's driver name (work items carry only its id). */
  driverTitle?: string | null;
}

export function WorkItemTypeInline({ item, showBadge = true, driverTitle }: InlineProps) {
  const hint = workItemTypeHint(item, driverTitle);
  if (!hint && !showBadge) return null;

  return (
    <span className="inline-flex items-center gap-1.5">
      {showBadge && <WorkItemTypeBadge type={item.type} variant="compact" />}
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      {item.type === 'Reading' && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <TrendingUp className="h-3 w-3 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent>Best scheduled in an afternoon or late-day block</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </span>
  );
}

export default WorkItemTypeBadge;
